/**
 * The family's two selectable axes, its geometry, and every string a reader can
 * see, in one place so five surfaces cannot state one fact five ways.
 *
 * The card axis is structural — the demonstration carries the card, the
 * coaching text carries it, or nothing but the prescription's figures do. The
 * move axis is editorial: the vendor narrates each movement twice, once as a
 * paragraph and once as numbered steps, and neither is derivable from the
 * other.
 *
 * The geometry constants below are consequences of one measurement, taken with
 * `curl` and `ffprobe` before anything in this folder was drawn. See
 * `C_WORKOUT_ASSET_PROBE`.
 */
/**
 * The family enum arrives from the kit's own variants LEAF rather than its
 * barrel, and the type from the barrel as usual.
 *
 * The barrel re-exports every surface in the kit, one of which imports a
 * stylesheet — and `cworkoutcard.model.ts` reads this module's copy while
 * running under `node --import tsx --test`, a runtime with no CSS loader. The
 * same split is already load-bearing in `Cwebresultcard`, for the same reason.
 */
import { CresultcardVariantFamily } from "@/components/ui/Cresultcard/cresultcard.variants";
import type { CresultcardVariantGroup } from "@/components/ui/Cresultcard";
import {
  CworkoutcardMetaSlot,
  CworkoutcardMuscleSlot,
  CworkoutcardMoveReading,
  CworkoutcardPhase,
  CworkoutcardPlanFact,
  CworkoutcardShoot,
  CworkoutcardVariant,
  CworkoutcardVideoState,
} from "./cworkoutcard.types";

export const C_WORKOUT_CARD_VARIANTS: readonly CworkoutcardVariant[] = [
  CworkoutcardVariant.CoachingCard,
  CworkoutcardVariant.DemoFrame,
  CworkoutcardVariant.LoadBoard,
];

/**
 * Coaching card leads, and the capture is why.
 *
 * The demonstration clip does not exist on a catalog row: the list call
 * publishes `hasVideo: true` and no address, and a playable URL only arrives
 * from `ymove.exerciseMedia`, a separate billed call made per exercise. In the
 * mounted three-row page exactly ONE row has a resolved clip, so a media-led
 * default presents two thirds of the set as a designed absence before a reader
 * has read a word. What separates one movement from another on a catalog page
 * is its name, its muscle, its equipment and its execution — none of which is a
 * picture. `Demo frame` is the composition for a row that HAS its clip, and it
 * is what the detail tier opens with.
 */
export const DEFAULT_C_WORKOUT_CARD_VARIANT = CworkoutcardVariant.CoachingCard;

export const C_WORKOUT_CARD_VARIANT_GROUP: CresultcardVariantGroup = {
  key: "pillar-widgets-workout-card",
  family: CresultcardVariantFamily.MediaAndCulture,
  label: "Workout cards",
  options: C_WORKOUT_CARD_VARIANTS,
  defaultValue: DEFAULT_C_WORKOUT_CARD_VARIANT,
};

export const C_WORKOUT_MOVE_READINGS: readonly CworkoutcardMoveReading[] = [
  CworkoutcardMoveReading.Steps,
  CworkoutcardMoveReading.Narration,
];

/**
 * Steps lead.
 *
 * Both readings are the vendor's own words, so this is a question about which
 * register a card should be in rather than which value is more complete. The
 * paragraph runs 219 to 349 characters on the captured rows and always opens
 * with the same two labels — `Starting position:` and `Execution:` — which is
 * prose doing a list's job at four times a list's length. The steps are four to
 * six lines of at most 77 characters, and a person mid-set reads a line, not a
 * paragraph.
 */
export const DEFAULT_C_WORKOUT_MOVE_READING = CworkoutcardMoveReading.Steps;

export const C_WORKOUT_MOVE_READING_GROUP: CresultcardVariantGroup = {
  key: "pillar-widgets-workout-move",
  family: CresultcardVariantFamily.MediaAndCulture,
  label: "Workout move reading",
  options: C_WORKOUT_MOVE_READINGS,
  defaultValue: DEFAULT_C_WORKOUT_MOVE_READING,
};


/**
 * The row's and the rail card's own names in the variant stamp. Neither is one
 * of the three card compositions, and neither varies with the one selected.
 */
export const C_WORKOUT_ROW_VARIANT = "Workout row";
export const C_WORKOUT_RAIL_VARIANT = "Workout rail card";
export const C_WORKOUT_PLAN_VARIANT = "Workout plan";

/** The BEM modifier each composition stamps beside the family root class. */
export const C_WORKOUT_CARD_MODIFIER: Record<CworkoutcardVariant, string> = {
  [CworkoutcardVariant.DemoFrame]: "cworkoutcard--demo",
  [CworkoutcardVariant.CoachingCard]: "cworkoutcard--coaching",
  [CworkoutcardVariant.LoadBoard]: "cworkoutcard--board",
};

export const C_WORKOUT_RAIL_MODIFIER = "cworkoutcard--rail";
export const C_WORKOUT_PLAN_MODIFIER = "cworkoutcard--plan";

/* ── Geometry ──────────────────────────────────────────────────────────────
 * PROBED 2026-08-28, before any of this was drawn. One `curl` for the poster
 * and one `ffprobe` for the clip, both against the captured pre-signed URLs.
 *
 * Three findings decided every frame in this folder.
 *
 * 1. The clip and its poster are BOTH 1280×720 — sixteen by nine, landscape,
 *    h264, thirty seconds exactly, range-served (HTTP 206, so the element can
 *    seek). The projection's own `orientation` field says `portrait` and its
 *    `videoDurationSecs` says `null`. The measurement wins for GEOMETRY and the
 *    vendor's word is printed as a stated fact beside it, because a frame drawn
 *    to a field the asset contradicts is a letterbox at best and a crop at
 *    worst.
 *
 * 2. 1280 is far wider than any column this family lives in, so the asset is
 *    never upscaled at any scale and the no-upscale ceiling never bites on
 *    width. It bites on HEIGHT: the kit's `Native` ceiling at `Hero` is 300px,
 *    and a 16:9 frame filling the 556px version-content column wants 312.75px.
 *    So the hero frame is capped at a WIDTH derived from that ceiling rather
 *    than left to trip it — see `C_WORKOUT_DETAIL_MEDIA`.
 *
 * 3. A catalog row has no poster at all. The list call publishes the three
 *    video BOOLEANS and no address, so the row's lead is its own index rather
 *    than a frame that would be empty on every row nobody paid to resolve.
 */

/** What the probe found, so a later reader can check it rather than trust it. */
export const C_WORKOUT_ASSET_PROBE =
  "probed 2026-08-28 · poster image/jpeg 1280×720 (HTTP 200, 33,571 bytes) · clip h264 1280×720, 30.000s (HTTP 206)";

/** The measured pixels of the captured poster frame. */
export const C_WORKOUT_POSTER_INTRINSIC_WIDTH = 1280;
export const C_WORKOUT_POSTER_INTRINSIC_HEIGHT = 720;

/**
 * The hero frame's ceiling as a width, derived from the measurement above and
 * the kit's own `Native` height ceiling at `Hero`: 300 × 1280 ÷ 720 = 533.33,
 * floored. At 533 the frame is exactly 300 tall, so the ceiling is reached and
 * never crossed — the alternative is a 556px frame the kit clamps to 300 and
 * stamps `contained`, which is 23px of bare plate on each side of a picture
 * that could have filled the box.
 */
export const C_WORKOUT_DETAIL_MEDIA = 533;

/**
 * The same ceiling as a WIDTH, which is a different thing from a number: the
 * detail is also a set's drill pane, and in the Cards layout that pane is the
 * card's own cell rather than the column the cap was chosen against.
 */
export const C_WORKOUT_DETAIL_MEDIA_WIDTH = `min(${String(C_WORKOUT_DETAIL_MEDIA)}px, 100%)`;

/** The deck card's own frame: 236px of rail card less the shell's 3px bezel. */
export const C_WORKOUT_RAIL_MEDIA = 230;

/** The lead frame on a text-first card, and on a plan's slot rows. */
export const C_WORKOUT_CARD_LEAD_MEDIA = 52;

/** The row's state column: the clip's own answer, or the word for its absence. */
export const C_WORKOUT_ROW_STATE = 74;
/** Between the state capsule and the open hint, inside the trailing zone. */
export const C_WORKOUT_ROW_FIGURE_GAP = 12;

/** How many chips a card shows before the row's own overflow control. */
export const C_WORKOUT_CHIP_LIMIT = 4;
/** Past this, a step list stops being steps and becomes a page. */
export const C_WORKOUT_CARD_STEP_LIMIT = 3;

/* ── Copy ──────────────────────────────────────────────────────────────────
 * Every reader-facing string this family authors. Everything else on the
 * surface is the vendor's own words.
 */

export const CWORKOUTCARD_EXERCISE_LABEL = "Exercise";
export const CWORKOUTCARD_PLAN_LABEL = "Workout";
export const CWORKOUTCARD_WATCH_LABEL = "Watch";
export const CWORKOUTCARD_STEPS_TITLE = "How it goes";
export const CWORKOUTCARD_POINTS_TITLE = "Coaching points";
export const CWORKOUTCARD_DEMONSTRATION_TITLE = "Demonstration";
export const CWORKOUTCARD_SECONDARY_MUSCLES_LABEL = "also works";
export const CWORKOUTCARD_SEE_ALL_TYPES_LABEL = "Show every type";
export const CWORKOUTCARD_MORE_STEPS_LABEL = "Show every step";
export const CWORKOUTCARD_FEWER_STEPS_LABEL = "Show fewer steps";
export const CWORKOUTCARD_PREMIUM_LABEL = "premium";
export const CWORKOUTCARD_REST_LABEL = "rest";
/** Stamped beside a demonstration whose links were SIGNED — never beside a
 *  durable address, which has no deadline to state. */
export const CWORKOUTCARD_SIGNED_UNTIL_LABEL = "signed until";
export const CWORKOUTCARD_SETS_LABEL = "sets";
export const CWORKOUTCARD_MINUTES_LABEL = "min";

/**
 * The word each ACTIONABLE demonstration state wears.
 *
 * `Playable` names the clip it leads. `Expired` and `Failed` are the two states
 * a reader can still do something about — ask for the movement again, or arm it
 * again. The two that are neither carry no entry and draw nothing: a movement
 * nobody filmed and a catalog row with no address are absences, and an absence
 * is not drawn.
 */
export const C_WORKOUT_VIDEO_STATE_LABEL: Partial<
  Record<CworkoutcardVideoState, string>
> = {
  [CworkoutcardVideoState.Playable]: "Demonstration",
  [CworkoutcardVideoState.Expired]: "Link expired",
  [CworkoutcardVideoState.Failed]: "Would not play",
};

/** Which shoot the clip came off, spelled for a reader rather than a slug. */
export const C_WORKOUT_SHOOT_LABEL: Record<CworkoutcardShoot, string> = {
  [CworkoutcardShoot.WhiteBackground]: "white background",
  [CworkoutcardShoot.GymShot]: "gym shot",
};

/** The three phases of a generated plan, in the order a plan runs them. */
export const C_WORKOUT_PHASE_ORDER: readonly CworkoutcardPhase[] = [
  CworkoutcardPhase.Warmup,
  CworkoutcardPhase.Main,
  CworkoutcardPhase.Cooldown,
];

/** The declared drop order for the row's meta line, tail first. */
export const C_WORKOUT_ROW_DROP_ORDER: readonly string[] = [
  CworkoutcardMetaSlot.Difficulty,
  CworkoutcardMetaSlot.Equipment,
];

/** The muscle line's drop order: the secondaries go, the primary never does. */
export const C_WORKOUT_MUSCLE_DROP_ORDER: readonly string[] = [
  CworkoutcardMuscleSlot.Secondary,
];

/** The plan header's drop order: its name already carries its muscles. */
export const C_WORKOUT_PLAN_DROP_ORDER: readonly string[] = [
  CworkoutcardPlanFact.Muscles,
  CworkoutcardPlanFact.Movements,
];

/** The two chips the record itself contributes, beside its own exercise types. */
export const C_WORKOUT_CATEGORY_CHIP_KEY = "workoutCategory";
export const C_WORKOUT_PREMIUM_CHIP_KEY = "workoutPremium";

/** The two nouns the withheld-steps line counts in. */
export const CWORKOUTCARD_STEP_NOUN = "step";
export const CWORKOUTCARD_STEPS_NOUN = "steps";
export const CWORKOUTCARD_STEPS_WITHHELD_SUFFIX = "in the opened pane";
/** What a plan and its phases count. */
export const CWORKOUTCARD_MOVEMENTS_NOUN = "movements";
